import { IntegrationExtras } from './admin/IntegrationExtras.js'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type ConnectorDef,
  type IntegrationConfigView,
  type PendingDocument,
} from '../app/api.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDate, formatDateTime } from '../framework/format.js'

/**
 * The accounting connection (docs/11 §Integration).
 *
 * Two halves, and they are separate on purpose. The top half is configuration
 * — which system, at what address, from what date. The bottom half is the
 * queue: what is waiting, what failed and why. A screen that only offers the
 * first is one where nobody ever finds out that nothing has synced since March.
 */

interface Draft {
  connector: string
  endpointUrl: string
  organisationId: string
  companyBook: string
  accessToken: string
  fromDate: string
  docTypes: string[]
  autoSend: boolean
}

const draftFrom = (config: IntegrationConfigView): Draft => ({
  connector: config.connector,
  endpointUrl: config.endpointUrl ?? '',
  organisationId: config.organisationId ?? '',
  companyBook: config.companyBook ?? '',
  // Never pre-filled: the stored token is not readable, and an empty box that
  // is left alone means "keep it".
  accessToken: '',
  fromDate: config.fromDate ?? '',
  docTypes: [...config.docTypes],
  autoSend: config.autoSend,
})

const QUEUE_FILTERS = [
  { value: '', label: 'Everything outstanding' },
  { value: 'PENDING', label: 'Waiting to be sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'STOPPED', label: 'Held back' },
  { value: 'COMPLETED', label: 'In the accounting system' },
  { value: 'OUT_OF_SCOPE', label: 'Before the start date' },
]

const STATE_WORDS: Readonly<Record<string, string>> = {
  NOT_APPLICABLE: 'Nothing to send',
  PENDING: 'Waiting',
  COMPLETED: 'Sent',
  FAILED: 'Failed',
  STOPPED: 'Held back',
  OUT_OF_SCOPE: 'Before start date',
}

export const IntegrationPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const client = useQueryClient()

  const connectors = useQuery({ queryKey: ['connectors'], queryFn: () => api.integrationConnectors() })
  const docTypes = useQuery({ queryKey: ['sync-doc-types'], queryFn: () => api.integrationDocTypes() })
  const config = useQuery({ queryKey: ['integration-config', api.companyId], queryFn: () => api.integrationConfig() })
  const summary = useQuery({ queryKey: ['integration-summary', api.companyId], queryFn: () => api.integrationSummary() })

  const [status, setStatus] = useState('')
  const queue = useQuery({
    queryKey: ['integration-queue', api.companyId, status],
    queryFn: () => api.integrationQueue(status === '' ? undefined : status),
  })

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [manual, setManual] = useState<{ id: string; documentNo: string | null; reference: string } | null>(null)

  useEffect(() => {
    if (config.data) setDraft(draftFrom(config.data))
  }, [config.data])

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['integration-config', api.companyId] })
    void client.invalidateQueries({ queryKey: ['integration-summary', api.companyId] })
    void client.invalidateQueries({ queryKey: ['integration-queue', api.companyId] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to save.')
      return api.saveIntegrationConfig({
        connector: draft.connector,
        endpointUrl: draft.endpointUrl,
        organisationId: draft.organisationId,
        companyBook: draft.companyBook,
        // Left blank the stored token is kept; typing one replaces it.
        ...(draft.accessToken === '' ? {} : { accessToken: draft.accessToken }),
        fromDate: draft.fromDate === '' ? null : draft.fromDate,
        docTypes: draft.docTypes,
        autoSend: draft.autoSend,
      })
    },
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const run = useMutation({
    mutationFn: () => api.runIntegration(),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const reassess = useMutation({
    mutationFn: () => api.reassessIntegration(),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const act = useMutation({
    mutationFn: ({ id, what, reference }: { id: string; what: 'requeue' | 'stop' | 'manual'; reference?: string }) =>
      what === 'requeue' ? api.requeueDocument(id)
        : what === 'stop' ? api.stopDocument(id)
          : api.markIntegratedByHand(id, reference ?? ''),
    onSuccess: () => { setError(null); setManual(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  if (config.isLoading || connectors.isLoading) return <Loading />
  if (config.error) return <ErrorBlock error={config.error} />
  if (!draft) return <Loading />

  const chosen: ConnectorDef | undefined = connectors.data?.find((c) => c.key === draft.connector)
  const needs = (key: string): boolean => (chosen?.fields ?? []).some((f) => f.key === key)

  const columns: ReadonlyArray<ColumnDef<PendingDocument>> = [
    {
      key: 'documentNo',
      header: 'Document',
      type: 'docno',
      width: 180,
      render: (row) => (
        <Link to={paths.document(companyId, row.route, row.id)}>{row.documentNo ?? '—'}</Link>
      ),
    },
    { key: 'docTypeLabel', header: 'Type', width: 180 },
    { key: 'documentDate', header: 'Date', width: 130, render: (r) => formatDate(r.documentDate) },
    { key: 'total', header: 'Value', type: 'money', width: 150 },
    {
      key: 'integrationStatus',
      header: 'State',
      width: 150,
      render: (r) => STATE_WORDS[r.integrationStatus] ?? r.integrationStatus,
    },
    { key: 'integrationRef', header: 'Reference', width: 170, render: (r) => r.integrationRef ?? '—' },
    { key: 'integrationError', header: 'What happened', render: (r) => r.integrationError ?? '—' },
    {
      key: 'actions',
      header: '',
      width: 250,
      render: (row) => (
        <div className="row">
          {row.integrationStatus === 'COMPLETED' ? null : (
            <Button kind="ghost" onClick={() => act.mutate({ id: row.id, what: 'requeue' })}>Send</Button>
          )}
          {row.integrationStatus === 'PENDING' || row.integrationStatus === 'FAILED' ? (
            <Button kind="ghost" onClick={() => act.mutate({ id: row.id, what: 'stop' })}>Hold</Button>
          ) : null}
          {row.integrationStatus === 'COMPLETED' ? null : (
            <Button
              kind="ghost"
              onClick={() => setManual({ id: row.id, documentNo: row.documentNo, reference: '' })}
            >
              Entered by hand
            </Button>
          )}
        </div>
      ),
    },
  ]

  const counts = summary.data?.counts

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <Panel
        title="The accounting system"
        aside={
          <div className="row">
            <Button kind="ghost" disabled={reassess.isPending} onClick={() => reassess.mutate()}>
              Re-check every document
            </Button>
            <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </div>
        }
      >
        <div className="form-grid">
          <Field label="Connected to" hint={chosen?.blurb}>
            <Select
              value={draft.connector}
              onChange={(connector) => setDraft({ ...draft, connector })}
              options={(connectors.data ?? []).map((c) => ({ value: c.key, label: c.label }))}
            />
          </Field>
          <Field
            label="Send documents dated from"
            hint="Anything earlier is marked as before the start date and never sent."
          >
            <Input
              type="date"
              value={draft.fromDate}
              onChange={(fromDate) => setDraft({ ...draft, fromDate })}
            />
          </Field>
          {needs('endpointUrl') ? (
            <Field
              label={chosen?.fields.find((f) => f.key === 'endpointUrl')?.label ?? 'Address'}
              required
              hint={chosen?.fields.find((f) => f.key === 'endpointUrl')?.hint}
            >
              <Input value={draft.endpointUrl} onChange={(endpointUrl) => setDraft({ ...draft, endpointUrl })} />
            </Field>
          ) : null}
          {needs('organisationId') ? (
            <Field label="Organisation id" required>
              <Input value={draft.organisationId} onChange={(organisationId) => setDraft({ ...draft, organisationId })} />
            </Field>
          ) : null}
          {needs('companyBook') ? (
            <Field label="Company name in the accounting system" required hint="Exactly as it is spelt there.">
              <Input value={draft.companyBook} onChange={(companyBook) => setDraft({ ...draft, companyBook })} />
            </Field>
          ) : null}
          {needs('accessToken') ? (
            <Field
              label="Access token"
              hint={config.data?.hasAccessToken
                ? 'A token is stored. Leave this blank to keep it; type a new one to replace it.'
                : 'Sent to the accounting system to authenticate. Stored on the server and never shown again.'}
            >
              <Input
                type="password"
                value={draft.accessToken}
                onChange={(accessToken) => setDraft({ ...draft, accessToken })}
                placeholder={config.data?.hasAccessToken ? '••••••••' : ''}
              />
            </Field>
          ) : null}
        </div>

        {draft.connector === 'NONE' ? null : (
          <>
            <label className="column-choice">
              <input
                type="checkbox"
                checked={draft.autoSend}
                onChange={() => setDraft({ ...draft, autoSend: !draft.autoSend })}
              />
              <span>Queue a document as soon as it is approved</span>
            </label>

            <h3 className="subhead" style={{ marginTop: 20 }}>Which documents go across</h3>
            <div className="column-choices">
              {(docTypes.data ?? []).map((type) => (
                <label className="column-choice" key={type.key}>
                  <input
                    type="checkbox"
                    checked={draft.docTypes.includes(type.key)}
                    onChange={() => setDraft({
                      ...draft,
                      docTypes: draft.docTypes.includes(type.key)
                        ? draft.docTypes.filter((k) => k !== type.key)
                        : [...draft.docTypes, type.key],
                    })}
                  />
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {config.data?.lastRunAt ? (
          <p className="helper" style={{ marginTop: 16 }}>
            Last run {formatDateTime(config.data.lastRunAt)} — {config.data.lastRunNote}
          </p>
        ) : null}
      </Panel>

      <IntegrationExtras connector={config.data?.connector ?? 'NONE'} />

      <Panel
        title="The queue"
        aside={
          <div className="row">
            <Select value={status} onChange={setStatus} options={QUEUE_FILTERS} />
            <Button
              kind="primary"
              disabled={run.isPending || draft.connector === 'NONE'}
              onClick={() => run.mutate()}
            >
              Send what is waiting
            </Button>
          </div>
        }
      >
        {counts ? (
          <p className="helper">
            {counts.PENDING} waiting · {counts.FAILED} failed · {counts.COMPLETED} sent ·
            {' '}{counts.STOPPED} held · {counts.OUT_OF_SCOPE} before the start date
            {summary.data?.oldestPendingDate
              ? ` · oldest waiting since ${formatDate(summary.data.oldestPendingDate)}`
              : ''}
          </p>
        ) : null}
        {run.data ? <Notice kind="info">{run.data.note}</Notice> : null}

        <Table
          columns={columns}
          rows={queue.data ?? []}
          getKey={(row) => row.id}
          empty={
            <Empty
              title="Nothing outstanding"
              body={
                draft.connector === 'NONE'
                  ? ['No accounting system is connected, so nothing is queued.']
                  : ['Every approved document has reached the accounting system.']
              }
            />
          }
        />
      </Panel>

      {manual ? (
        <Modal
          title="Entered by hand"
          subtitle={`Record that ${manual.documentNo ?? 'this document'} was keyed into the accounting system.`}
          actions={
            <>
              <Button kind="ghost" onClick={() => setManual(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={manual.reference.trim() === '' || act.isPending}
                onClick={() => act.mutate({ id: manual.id, what: 'manual', reference: manual.reference })}
              >
                Record it
              </Button>
            </>
          }
          onClose={() => setManual(null)}
        >
          <Field
            label="Reference in the accounting system"
            required
            hint="The voucher or bill number it was given there, so the two can be tied together later."
          >
            <Input
              value={manual.reference}
              onChange={(reference) => setManual({ ...manual, reference })}
            />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}
